// features/overview/overviewSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getOverview, getOverviewHistory } from '@/api';

import {
  IOverviewBestPerformerHistoryRecord,
  IOverviewEmployeeLeaderboardHistoryRecord,
  IOverviewHistoryRequestParams,
  IOverviewHistoryResponse,
  IOverviewMonthlyKpiRecord,
  IOverviewProjectsLaunchedHistoryRecord,
  IOverviewRequestParams,
  IOverviewResponse,
  IOverviewSpeedOfDeliveryHistoryRecord,
  IOverviewState,
  ITeamLeadHistoryRecord,
} from '@/interface/overview.interface';

import { OVERVIEW_HISTORY_PAGE_SIZE } from '@/pages/dashboard/overview/constants/overview.constant';

import { OverviewHistoryType } from '@/enum/overview.enum';

const emptyMonthlyKpi = {
  current: 0,
  records: [],
};

const initialState: IOverviewState = {
  loading: false,
  error: null,
  stats: {
    total: 0,
    active: 0,
    completed: 0,
    paused: 0,
  },
  projects: [],
  projectsLaunched: [],
  projectsLaunchedHistory: [],
  monthlyKpis: {
    requested: emptyMonthlyKpi,
    completed: emptyMonthlyKpi,
    delivered: emptyMonthlyKpi,
    activeProjects: emptyMonthlyKpi,
  },
  teamLeadsPerformance: [],
  teamLeadsPerformanceHistory: [],
  currentMonthEmployees: [],
  employeeLeaderboardHistory: [],
  bestPerformerHistory: [],
  performancePerMonth: [],
  speedOfDelivery: [],
  speedOfDeliveryHistory: [],
  historyPagination: {
    page: 1,
    pageSize: OVERVIEW_HISTORY_PAGE_SIZE,
    totalMonths: 0,
  },
  selectedMonth: null,
  selectedYear: null,
};

export const getOverviewRequest = createAsyncThunk<IOverviewResponse, IOverviewRequestParams | void, { rejectValue: string }>(
  'overview/fetch',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getOverview(params || undefined);
      return response.data;
    } catch (error) {
      const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
      return rejectWithValue(message || 'Failed to fetch overview');
    }
  },
);

export const getOverviewHistoryRequest = createAsyncThunk<
  IOverviewHistoryResponse,
  IOverviewHistoryRequestParams,
  { rejectValue: string }
>('overview/fetchHistory', async (params, { rejectWithValue }) => {
  try {
    const response = await getOverviewHistory(params);
    return response.data;
  } catch (error) {
    const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
    return rejectWithValue(message || 'Failed to fetch overview history');
  }
});
export const overviewSlice = createSlice({
  name: 'overview',
  initialState,
  reducers: {
    resetOverviewError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOverviewRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOverviewRequest.fulfilled, (state, action) => {
        const {
          bestPerformerHistory,
          currentMonthEmployees,
          employeeLeaderboardHistory,
          monthlyKpis,
          performancePerMonth,
          projects,
          projectsLaunched,
          projectsLaunchedHistory,
          historyPagination,
          selectedMonth,
          selectedYear,
          speedOfDelivery,
          speedOfDeliveryHistory,
          stats,
          teamLeadsPerformance,
          teamLeadsPerformanceHistory,
        } = action.payload;
        state.loading = false;
        state.projects = Array.from(
          new Map((projects ?? []).filter((project) => project?.id).map((project) => [project.id, project])).values(),
        );
        state.projectsLaunched = projectsLaunched ?? [];
        state.projectsLaunchedHistory = projectsLaunchedHistory ?? [];
        state.monthlyKpis = monthlyKpis ?? initialState.monthlyKpis;
        state.teamLeadsPerformance = teamLeadsPerformance ?? [];
        state.teamLeadsPerformanceHistory = teamLeadsPerformanceHistory ?? [];
        state.currentMonthEmployees = currentMonthEmployees ?? [];
        state.employeeLeaderboardHistory = employeeLeaderboardHistory ?? [];
        state.bestPerformerHistory = bestPerformerHistory ?? [];
        state.performancePerMonth = performancePerMonth ?? [];
        state.speedOfDelivery = speedOfDelivery ?? [];
        state.speedOfDeliveryHistory = speedOfDeliveryHistory ?? [];
        state.historyPagination = historyPagination ?? initialState.historyPagination;
        state.selectedMonth = selectedMonth ?? null;
        state.selectedYear = selectedYear ?? null;
        state.stats = stats;
      })
      .addCase(getOverviewRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      })
      .addCase(getOverviewHistoryRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOverviewHistoryRequest.fulfilled, (state, action) => {
        const { data, metric, pagination, selectedMonth, selectedYear, type } = action.payload;
        state.loading = false;
        state.historyPagination = pagination;
        state.selectedMonth = selectedMonth;
        state.selectedYear = selectedYear;

        switch (type) {
          case OverviewHistoryType.MONTHLY_KPIS:
            if (metric) {
              state.monthlyKpis = {
                ...state.monthlyKpis,
                [metric]: {
                  ...state.monthlyKpis[metric],
                  records: data as IOverviewMonthlyKpiRecord[],
                },
              };
            }
            break;
          case OverviewHistoryType.PROJECTS_LAUNCHED:
            state.projectsLaunchedHistory = data as IOverviewProjectsLaunchedHistoryRecord[];
            break;
          case OverviewHistoryType.EMPLOYEE_LEADERBOARD:
            state.employeeLeaderboardHistory = data as IOverviewEmployeeLeaderboardHistoryRecord[];
            break;
          case OverviewHistoryType.BEST_PERFORMER:
            state.bestPerformerHistory = data as IOverviewBestPerformerHistoryRecord[];
            break;
          case OverviewHistoryType.TEAM_LEADS:
            state.teamLeadsPerformanceHistory = data as ITeamLeadHistoryRecord[];
            break;
          case OverviewHistoryType.SPEED_OF_DELIVERY:
            state.speedOfDeliveryHistory = data as IOverviewSpeedOfDeliveryHistoryRecord[];
            break;
          default:
            break;
        }
      })
      .addCase(getOverviewHistoryRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });
  },
});
export const { resetOverviewError } = overviewSlice.actions;
